export const PagePrivacyPolicy = () => {
  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-kds-bg-primary">
      <div className="mx-auto w-full max-w-[820px] px-4 py-6 text-kds-fg sm:px-6 sm:py-10 lg:px-8 lg:py-14">
        <h1 className="text-2xl font-bold tracking-tight text-kds-fg sm:text-3xl">Privacy Policy</h1>
        <p className="mb-6 mt-2 text-xs text-kds-fg-soft sm:mb-8 sm:text-sm">Last updated: Jan 1, 2026</p>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          We are committed to protecting and respecting your privacy. This policy explains our practices concerning the
          personal data we collect from you, or that you provide to us. We endeavor to express ourselves in simple and
          concise language to ensure easy understanding. Important clauses related to your rights will be highlighted
          using bold and underlined text in this policy to draw your attention.If you do not agree with this policy, you
          should not use the Platform.
        </p>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          If you have any questions about how we use your personal data, contact us at:{" "}
          <a
            href="mailto:kaloclip@kalowave.com"
            className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
          >
            kaloclip@kalowave.com
          </a>
          .
        </p>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          1. What information do we collect about you?
        </h2>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          We collect and process information you give us when you create an account and upload content to the Platform.
          This includes technical and behavioural information about your use of the Platform. We also collect
          information about you if you interact with the Platform without creating an account.
        </p>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          2. How will we use the information about you?
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            2.1 To personalize your experience and to allow us to deliver the type of content and product offerings in
            which you are most interested;
          </li>
          <li className="list-disc">2.2 To manage your account and subscription;</li>
          <li className="list-disc">2.3 To improve our features of Service and in order to better serve you;</li>
          <li className="list-disc">
            2.4 Getting in touch with you and sending you notifications, newsletters, and commercial communications.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          3. How long do we keep hold of your information?
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            3.1 We retain your information for as long as it is necessary to provide you with the service.
          </li>
          <li className="list-disc">
            3.2 Where we do not need your information in order to provide the service to you, we retain it only as long
            as we have a legitimate business purpose in keeping such data or where we are subject to a legal obligation
            to retain the data.
          </li>
          <li className="list-disc">
            3.3 We will also retain your data if we believe it is or will be necessary for the establishment, exercise
            or defence of legal claims.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          4. How will we notify you of any changes to this Privacy Policy?
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            4.1 We will generally notify all users of any material changes to this policy through a notice on our
            Platform. However, you should look at this policy regularly to check for any changes.
          </li>
          <li className="list-disc">
            4.2 We will also update the "Last Updated" date at the top of this policy, which reflects the effective date
            of such policy.
          </li>
          <li className="list-disc">
            4.3 By accessing or using the Platform, you acknowledge that you have read this policy and that you
            understand your rights in relation to your personal data and agree that how we will collect, use and process
            it.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          5. The types of personal data we use
        </h2>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          We collect and use the following information about you:
        </p>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            5.1 Profile Information. You give us information when you register on the Platform, including your telephone
            number, your industry and your identity.
          </li>
          <li className="list-disc">
            5.2 User Content and Behavioural Information. We collect information regarding your use of the Platform,
            e.g., how you engage with the Platform.
          </li>
          <li className="list-disc">
            5.3 Technical Information we collect about you. We collect certain information about the device you use to
            access the Platform, such as your IP address, user agent, mobile carrier, time zone settings, identifiers
            for advertising purposes, model of your device, the device system, network type, device IDs, your screen
            resolution and operating system, app and file names and types, keystroke patterns or rhythms, battery state,
            audio settings and connected audio devices. Where you log-in from multiple devices, we will be able to use
            your profile information to identify your activity across devices. We may also associate you with
            information collected from devices other than those you use to log-in to the Platform.
          </li>
          <li className="list-disc">
            5.4 Location. We collect information about your approximate location, including location information based
            on your SIM card and/or IP address. With your permission, we may also collect precise location data (such as
            GPS).
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">6. Cookies</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            6.1 We and our vendors and service providers use cookies and other similar technologies (e.g., web beacons,
            flash cookies, etc.) ("Cookies") to automatically collect information, measure and analyze which web pages
            you click on and how you use the Platform, enhance your experience using the Platform, improve our services,
            and provide you with targeted advertising on the Platform and elsewhere across your devices. Cookies enable
            the Platform to provide certain features and functionality. Web beacons are very small images or small
            pieces of data embedded in images, also known as "pixel tags" or "clear GIFs," that can recognize Cookies,
            the time and date a page is viewed, a description of the page where the pixel tag is placed, and similar
            information from your computer or device. By using the Platform, you consent to our use of Cookies.
          </li>
          <li className="list-disc">
            6.2 You can choose to have your computer warn you each time a cookie is being sent, or you can choose to
            turn off all cookies. You do this through your browser settings. Since browser is a little different, look
            at your browser's Help Menu to learn the correct way to modify your cookies.If you turn cookies off, some
            features will be disabled. It won't affect the user's experience, making your site experience more efficient
            and may not function properly.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          7. The security of your personal data
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            7.1 We take steps to ensure that your information is treated securely and in accordance with this policy.
          </li>
          <li className="list-disc">
            7.2 You are fully aware that the transmission of information via the internet is not completely secure.
            Although we will protect your personal data, for example, by encryption, we cannot guarantee the security of
            your information transmitted via the Platform; any transmission is at your own risk.
          </li>
          <li className="list-disc">
            7.3 We maintain these technical and organizational measures and will amend them from time to time to improve
            the overall security of our systems.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          8. Personal Inormation Rights
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            8.1 You have the right to access and modify your personal information at any time. If you no longer use our
            products or services, or if you deactivate your account, or if we collect or process your information in
            violation of legal regulations or without obtaining your consent, you can request the deletion of your
            personal information. We will promptly respond to such requests or anonymize the information.
          </li>
          <li className="list-disc">
            8.2 Some features of our relevant products and services require the provision of basic personal information
            based on your authorized consent. You have the right to withdraw (or stop) such authorization at any time.
            Once you withdraw your consent, we will no longer be able to provide the corresponding features or process
            the relevant personal information. However, your decision to withdraw consent will not affect any prior
            processing that was based on your consent.
          </li>
          <li className="list-disc">
            8.3 You have the right to submit an account deactivation request to us at any time. You can contact customer
            service to deactivate the account you previously registered.
          </li>
          <li className="list-disc">
            8.4 If you are dissatisfied with the way we handle your personal information, you can file a complaint with
            us at any time.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          9. Teens' Personal Information Protection
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            9.1 Without the consent of their guardians, teens should not create personal information accounts. In
            situations where personal information of teens is collected with the consent of their guardians, we will
            prioritize the protection of their personal information in accordance with relevant laws and regulations.
          </li>
          <li className="list-disc">
            9.2 If we discover that we have collected personal information of teens without obtaining verifiable consent
            from their guardians beforehand, we will promptly delete the relevant data.
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">10. Others</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            10.1 You acknowledge and agree that this Privacy Policy is an integral part of the relevant kaloclip Terms
            of Service.
          </li>
          <li className="list-disc">
            10.2 Your access to and use of our Services is also subject to the terms of our partners including but not
            limited to the{" "}
            <a
              href="https://www.tiktok.com/legal/page/global/tik-tok-developer-terms-of-service/en"
              target="_blank"
              rel="noopener noreferrer"
              className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
            >
              TikTok Developer Terms of Service
            </a>
            , the terms of which can be found directly on the Platform or on our respective partner's platform.
          </li>
          <li className="list-disc">
            10.3 If you have questions, comments, or requests regarding this policy, contact us at:{" "}
            <a
              href="mailto:kaloclip@kalowave.com"
              className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
            >
              kaloclip@kalowave.com
            </a>
            .
          </li>
        </ul>

        <h1 className="mt-10 text-2xl font-bold tracking-tight text-kds-fg sm:text-3xl">隐私政策</h1>
        <p className="mb-6 mt-2 text-xs text-kds-fg-soft sm:mb-8 sm:text-sm">最后更新：2026 年 1 月 1 日</p>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          我们致力于保护和尊重您的隐私。本政策解释了关于我们从您那里收集或使用您提供给我们的个人数据的做法。我们努力用通俗易懂、简明扼要的文字进行表达，并对本政策中与您的权益密切相关的条款采用粗体字、下划线进行标注以提示您注意。如果您不同意本政策，则不应使用本平台。
        </p>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          如果您对我们如何使用您的个人数据有任何疑问，请通过以下方式联系我们：{" "}
          <a
            href="mailto:kaloclip@kalowave.com"
            className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
          >
            kaloclip@kalowave.com
          </a>
          。
        </p>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          一、我们收集关于您的哪些信息？
        </h2>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          当您创建帐户并将内容上传到平台时，我们会收集和处理您提供给我们的信息。包括有关您使用平台的技术和行为信息，如果您在未创建帐户的情况下与平台互动，我们也会收集有关您的信息。
        </p>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          二、我们将如何使用您的信息？（是否会进行共享？转让？披露？）
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">2.1 为您提供个性化的服务体验，向您推荐您最感兴趣的内容和产品类型；</li>
          <li className="list-disc">2.2 管理您的帐户和订阅；</li>
          <li className="list-disc">2.3 改进我们的服务功能，更好地为您服务；</li>
          <li className="list-disc">2.4 与您取得联系并向您发送通知、新闻通讯和商业通讯。</li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          三、我们会保留您的信息多长时间？
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">3.1 只要您还在使用我们的服务，我们就会保留您的信息。</li>
          <li className="list-disc">
            3.2
            在我们不需要您的信息来向您提供服务的情况下，我们仅在我们有合法的商业目的来保留此类数据或我们有法律义务保留这些数据的情况下才会保留这些信息。
          </li>
          <li className="list-disc">
            3.3 如果我们认为建立、行使或辩护法律索赔是必要的或将是必要的，我们还将保留您的数据。
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          四、我们将如何通知您本隐私政策的更改？
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            4.1
            对于本政策的任何重大变更，我们通常会通过我们平台上通知所有用户。同时，您应该定期查看此政策以检查是否有任何更改。
          </li>
          <li className="list-disc">4.2 我们将更新本政策顶部的“最后更新”日期，该日期反映了该政策的生效日期。</li>
          <li className="list-disc">
            4.3 通过访问或使用平台，您承认您已阅读本政策，您已了解并同意我们收集、使用和处理这些数据的方式和后果。
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">五、我们使用的个人数据类型</h2>
        <p className="mb-3 text-sm leading-6 text-kds-fg-soft sm:mb-4 sm:text-[15px] sm:leading-7">
          我们将收集和使用关于您的以下信息：
        </p>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            5.1 个人资料信息。您在平台上注册时向我们提供的信息，包括您的电话号码、您的行业和您的身份等。
          </li>
          <li className="list-disc">5.2 用户内容和行为信息。我们收集有关您使用平台的信息，例如，您如何与平台互动。</li>
          <li className="list-disc">
            5.3 技术信息。我们收集有关您用于访问平台的设备的某些信息，例如您的 IP
            地址、用户代理、移动运营商、时区设置、用于广告目的的标识符、您的设备型号、设备系统、网络类型、设备
            ID、您的屏幕分辨率和操作系统、应用程序和文件的名称和类型、击键模式或节奏、电池状态、音频设置和连接的音频设备。如果您从多个设备登录，我们将能够使用您的个人资料信息来识别您跨设备的活动。我们还可能将您与从您用于登录平台的设备以外的设备收集的信息相关联。
          </li>
          <li className="list-disc">
            5.4地点。我们收集有关您的大致位置的信息，包括基于您的 SIM 卡和/或 IP
            地址的位置信息。在您允许的情况下，我们还可能收集精确的位置数据（例如 GPS）。
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">六、Cookies</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            6.1 我们和我们的供应商和服务提供商使用 cookie 和其他类似技术（例如，网络信标、flash cookie
            等）（“cookies”）来自动收集信息、衡量和分析您点击了哪些网页以及您如何使用平台，增强您使用平台的体验，改进我们的服务，并在平台和您的设备上的其他地方为您提供有针对性的广告。
            Cookie使平台能够提供某些特性和功能。网络信标是非常小的图像或嵌入图像的小块数据，也称为“像素标签”或“清晰的
            GIF”，可以识别
            Cookie、查看页面的时间和日期、像素所在页面的描述标签已放置，并且来自您的计算机或设备的类似信息。通过使用平台，您同意我们使用
            Cookie。
          </li>
          <li className="list-disc">
            6.2 您可以选择让您的计算机在每次发送 cookie 时向您发出警告，或者您可以选择关闭所有
            cookie。您可以通过浏览器设置执行此操作。由于浏览器略有不同，请查看浏览器的帮助菜单以了解修改 cookie
            的正确方法。如果您关闭
            cookie，某些功能将被禁用。它不会影响用户的体验，使您的网站体验更加高效，并且可能无法正常运行。
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">七、您个人数据的安全性</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">7.1 我们将采取措施确保您的信息得到安全处理并符合本政策。</li>
          <li className="list-disc">
            7.2
            即便我们会通过加密等方式来保护您的个人数据，但我们不能保证您通过平台传输的信息的安全性；您充分了解并知晓通过互联网传输信息并不绝对安全，任何传输都需要您自担风险。
          </li>
          <li className="list-disc">7.3 我们将定期对我们的技术及措施进行维护，以提高我们系统的整体安全性。</li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">
          八、您享有的个人信息主体权利
        </h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            8.1
            您随时可以访问、修改您的个人信息，当您不再使用我们的产品或服务，或您注销账号后，或者我们搜集处理您的信息违反法律规定或未征求您同意时，您可以向我们提出删除个人信息的请求，我们将及时响应或作匿名化处理。
          </li>
          <li className="list-disc">
            8.2
            我们相关产品和服务功能需要基于您授权同意提供一些基本个人信息才能得以完成。您可以在授权后随时撤回（或停止）对该权限的继续授权。当您撤回同意后，我们无法继续为您提供对应功能，也不再处理相应的个人信息。但您撤回同意的决定，不会影响此前基于您的授权而开展的个人信息处理。
          </li>
          <li className="list-disc">8.3 您有权随时向我们提出账号注销申请，您可以通过联系客服注销您此前注册的账号。</li>
          <li className="list-disc">8.4 如果您对我们处理您个人信息的方式不满意，随时可以向我们进行投诉。</li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">九、未成年人的个人信息处理</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">
            9.1
            如果没有监护人的同意，未成年人不应创建自己的个人信息主体账号。对于经监护人同意而收集未成年人个人信息的情况，我们会根据相关法律法规的规定着重保护未成年人的个人信息。
          </li>
          <li className="list-disc">
            9.2 如果我们发现自己在未事先获得可证实的监护人同意的情况下收集了未成年人的个人信息，则会尽快删除相关数据。
          </li>
        </ul>
        <h2 className="mb-2 mt-6 text-base font-semibold text-kds-fg sm:mt-8 sm:text-lg">十、其他</h2>
        <ul className="space-y-1 pl-4 text-sm leading-6 text-kds-fg-soft sm:space-y-2 sm:pl-6 sm:text-[15px] sm:leading-7">
          <li className="list-disc">10.1 您知晓并同意本隐私政策系kaloclip相关服务条款不可分割的一部分。</li>
          <li className="list-disc">
            10.2 您访问或者使用我们的服务亦受我们合作伙伴的条款约束，包括但不限于
            <a
              href="https://www.tiktok.com/legal/page/global/tik-tok-developer-terms-of-service/en"
              target="_blank"
              rel="noopener noreferrer"
              className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
            >
              TikTok开发者服务条款
            </a>
            ，其条款可直接在我们平台或着我们合作伙伴的平台下载。
          </li>
          <li className="list-disc">
            10.3 如果您对本政策有任何疑问、意见或要求，请通过以下方式联系我们：
            <a
              href="mailto:kaloclip@kalowave.com"
              className="py-0.5 text-kds-link underline underline-offset-2 break-all sm:break-normal"
            >
              kaloclip@kalowave.com
            </a>
            。
          </li>
        </ul>
      </div>
    </div>
  );
};
